import { logException } from '@atlassian/ptc-common';
export const getLocationHashAndSearch = location => `${location.hash}${location.search}`;

/**
 * Extract the domain from the URL. Strips "www" subdomains
 * e.g. http://dogs.animals.com/yellow/golden-retriever becomes "dogs.animals.com"
 * e.g. http://www.blake.com/memes becomes "blake.com"
 *
 * Return an empty string if a totally invalid url is somehow passed in.
 * @param url
 */
export const getDomainFromLinkUri = url => {
  let hostname;
  try {
    try {
      hostname = unsafeGetDomainFromUrl(url);
    } catch (noProtocolError) {
      /**
       * Backend (Legion) validates whether the string is URI compliant. But URL expects
       * the string to have a protocol. This is not validated by URI compliancy. So
       * following is just a quick fix just for display purposes.
       */
      hostname = unsafeGetDomainFromUrl('http://' + url);
    }
  } catch (error) {
    return '';
  }
  return hostname.replace(/^www\./, '');
};

/**
 * Extract the domain from the URL.
 * Throws if url param is not a valid URL.
 * Marked as unsafe because the URL constructor throws Type Errors when it
 * receives invalid URLs. Feel free to use this method with adequate care.
 */
export const unsafeGetDomainFromUrl = url => {
  const urlObj = new URL(url);
  return urlObj.hostname;
};

/**
 * Extract the domain from the email.
 */
export const extractDomainFromEmail = email => {
  const parts = email.split('@');
  if (parts.length === 1) {
    return null;
  } else if (parts.length === 2 && !parts[0]) {
    return null;
  } else if (!parts[parts.length - 1]) {
    return null;
  }
  return parts[parts.length - 1];
};

/**
 * Takes a fully qualified URL `url` and returns a relative URL
 * by removing protocol and host.
 * Returns input `url` if it is not a fully qualified URL.
 */
export const getRelativeUrl = url => {
  if (!isAbsoluteLink(url)) {
    return url;
  }
  try {
    const {
      pathname,
      search,
      hash
    } = new URL(url);
    return `${pathname}${search}${hash}`;
  } catch (error) {
    logException(error, 'Cannot parse URL in getRelativeUrl', {
      url
    });
  }
  return url;
};
const isAbsoluteLink = url => {
  return url.startsWith('http') || url.startsWith('www');
};

/**
 * For a given arbitrary path, we will prefix it with a context path of People page.
 * For example: Em-Dir in Confluence
 * - A given path is `hello-world/123`
 * - Output will be `/wiki/people/hello-world/123`
 */
export const prefixWithContextEntryPoint = (path, contextEntryPoint = '') => {
  if (isAbsoluteLink(path) ||
  // do not prefix for absolute URL
  contextEntryPoint && path.startsWith(contextEntryPoint) ||
  // do not prefix if the link already has product context path
  path.startsWith('/')) {
    return path;
  }
  return `${contextEntryPoint}/${path}`;
};

/**
 * This function will check a link is SPA link or not basing on context path of product (Confluence or Jira).
 * If a given url is an absolute link, we check it by using host name and product context path.
 * If a given url is a relative link, there are 2 possible cases:
 * - `/<product-context-path>/link/path`: is a SPA link
 * - `link/path`: is a SPA link
 * - `/link/path`: is not a SPA link
 */
export const isSPALink = (url, contextSpaPaths) => {
  if (!contextSpaPaths || !contextSpaPaths.length) {
    return false;
  }
  let isStartWithContextSPA;
  if (isAbsoluteLink(url)) {
    try {
      const {
        pathname,
        search,
        hash,
        origin
      } = new URL(url);
      const relativeUrl = `${pathname}${search}${hash}`;
      const isSameDomain = window.location.href.includes(origin);
      isStartWithContextSPA = !!contextSpaPaths.find(contextPath => {
        return relativeUrl.startsWith(contextPath);
      });
      return isStartWithContextSPA && isSameDomain;
    } catch (error) {
      logException(error, 'Cannot parse URL in isSPALink', {
        url
      });
      return false;
    }
  }

  // is relative link
  isStartWithContextSPA = !!contextSpaPaths.find(contextPath => {
    return url.startsWith(contextPath);
  });
  return isStartWithContextSPA || !url.startsWith('/');
};